import React from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import { Invoice } from '../../modules/invoice/types';

// Register standard fonts if needed, but standard Helvetica is default and reliable.

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: '#000000',
    backgroundColor: '#ffffff',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 5,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold', // Helvetica supports bold
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 12,
    backgroundColor: '#e0f2f1', // Light teal
    color: '#00695c',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 'bold',
  },
  statusBadgeWarning: {
    paddingVertical: 4,
    paddingHorizontal: 12,
    backgroundColor: '#f5d488ff', // Light teal
    color: '#efad13ff',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 'bold',
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  metaText: {
    fontSize: 10,
    color: '#666666',
  },
  box: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 4,
    padding: 15,
    marginBottom: 20,
  },
  facilityName: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  facilityAddress: {
    fontSize: 10,
    color: '#666666',
    marginBottom: 10,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    marginVertical: 10,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  detailLabel: {
    color: '#555555',
    width: 150,
  },
  detailValue: {
    fontWeight: 'bold',
    textAlign: 'right',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  table: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    marginBottom: 20,
    borderRadius: 4,
    overflow: 'hidden', // to clip header corners
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    minHeight: 30, // Minimum height for row
    alignItems: 'center',
  },
  tableHeader: {
    backgroundColor: '#5f847d',
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    minHeight: 30,
    alignItems: 'center',
  },
  tableCellHeader: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
    padding: 8,
  },
  tableCell: {
    padding: 8,
    fontSize: 10,
    color: '#000000',
  },
  // Column widths (Total 100%)
  colDate: { width: '15%' },
  colWorker: { width: '45%' }, // Worker Name + Role
  colHours: { width: '25%' },
  colTotal: { width: '15%', textAlign: 'right' },

  footerBox: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 4,
    padding: 15,
  },
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 5,
    alignItems: 'center',
  },
  totalLabel: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  totalValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#5f847d',
  },
  thankYou: {
    marginTop: 30,
    textAlign: 'center',
    color: '#666666',
    fontSize: 10,
  }
});

interface InvoicePDFProps {
  invoice: Invoice;
  subTotal: number;
  taxAmount: number;
  totalAmount: number;
  gstPercentage: number;
  facilityDetails?: {
    name: string;
    address: string;
  };
}

const InvoicePDF: React.FC<InvoicePDFProps> = ({
  invoice,
  subTotal,
  taxAmount,
  totalAmount,
  gstPercentage,
  facilityDetails
}) => {
  console.log(invoice)
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headerRow}>
          <Text style={styles.headerTitle}>INVOICE</Text>
          <Text
            style={
              (invoice.payment_status === "pending" ? styles.statusBadgeWarning : styles.statusBadge)
            }
          >{invoice.payment_status.toUpperCase()}</Text>
        </View>
        <View style={styles.metaRow}>
          <Text style={styles.metaText}>Invoice No: {invoice.invoice_number}</Text>
          <Text style={styles.metaText}>Date: {dayjs(invoice.invoice_date).format('DD/MM/YYYY')}</Text>
        </View>

        {/* Facility Info Box */}
        <View style={styles.box}>
          <Text style={styles.facilityName}>{facilityDetails?.name || (invoice as any).facility_name || 'Facility Name'}</Text>
          <Text style={styles.facilityAddress}>{facilityDetails?.address || (invoice as any).facility_address || ''}</Text>
          <View style={styles.divider} />
          <View>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Invoice No:</Text>
              <Text style={styles.detailValue}>{invoice.invoice_number}</Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Period Covered:</Text>
              <Text style={styles.detailValue}>{dayjs(invoice.period_start_from).format('DD/MM/YYYY')} - {dayjs(invoice.period_end_to).format('DD/MM/YYYY')}</Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Date Issued:</Text>
              <Text style={styles.detailValue}>{dayjs(invoice.invoice_date).format('DD/MM/YYYY')}</Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Due Date:</Text>
              <Text style={styles.detailValue}>{dayjs(invoice.invoice_due_date).format('DD/MM/YYYY')}</Text>
            </View>
          </View>
        </View>

        {/* Workers List Table */}
        <Text style={styles.sectionTitle}>Workers Shift List</Text>
        <View style={styles.table}>
          {/* Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCellHeader, styles.colDate]}>Date</Text>
            <Text style={[styles.tableCellHeader, styles.colWorker]}>Worker / Role</Text>
            <Text style={[styles.tableCellHeader, styles.colHours]}>Hours / Rate</Text>
            <Text style={[styles.tableCellHeader, styles.colTotal]}>Total</Text>
          </View>
          {/* Rows */}
          {invoice.facility_invoice_items?.map((item) => (
            <React.Fragment key={item.invoice_item_id}>
              {item.payment_facility_invoice?.map((payment, idx) => {
                const hours = parseFloat(payment.hours_worked as unknown as string);
                const amount = parseFloat(payment.amount as unknown as string);
                const rate = hours > 0 ? (amount / hours).toFixed(2) : '0.00';

                return (
                  <View style={styles.tableRow} key={`${item.invoice_item_id}-${idx}`}>
                    <Text style={[styles.tableCell, styles.colDate]}>
                      {dayjs(item.shift.start_date_time).format('DD/MM/YYYY')}
                    </Text>
                    <View style={[styles.tableCell, styles.colWorker]}>
                      <Text style={{ fontWeight: 'bold', fontSize: 10 }}>{payment.worker.user.first_name} {payment.worker.user.last_name}</Text>
                      <Text style={{ fontSize: 9, color: '#666' }}>{item.shift.position_role}</Text>
                    </View>
                    <Text style={[styles.tableCell, styles.colHours]}>
                      {hours.toFixed(2)}h / ${rate}/h
                    </Text>
                    <Text style={[styles.tableCell, styles.colTotal]}>
                      ${amount.toFixed(2)}
                    </Text>
                  </View>
                );
              })}
            </React.Fragment>
          ))}
        </View>

        {/* Footer Box */}
        <View style={styles.footerBox}>
          <View style={styles.footerRow}>
            <Text>Sub Total:</Text>
            <Text style={{ fontWeight: 'bold' }}>${subTotal.toFixed(2)}</Text>
          </View>
          <View style={styles.footerRow}>
            <Text>GST ({gstPercentage.toFixed(0)}%):</Text>
            <Text style={{ fontWeight: 'bold' }}>${taxAmount.toFixed(2)}</Text>
          </View>
          <View style={[styles.divider, { borderBottomWidth: 2, borderBottomColor: '#000' }]} />
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Total Amount:</Text>
            <Text style={styles.totalValue}>${totalAmount.toFixed(2)}</Text>
          </View>
        </View>

        <Text style={styles.thankYou}>Thank you for your business!</Text>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
